import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import CustomHelmet from 'components/CustomHelmet';
import styled from 'styled-components';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { selectLocale } from 'models/localization';
import {
  selectAllLiveWebinars, selectAllOnDemandWebinars, selectWebinarById, webinarSortFn,
} from 'models/webinars';
import { selectLocalizedEventName } from 'models/virtual_event';
import { GroupChatType } from 'models/twilio';
import { useTranslation } from 'react-i18next';
import FloatingFooter from 'components/footer_v2/FloatingFooter';
import Routes from 'Routes';
import { fetchAuditoriumById, selectAuditoriumByWebinarId } from 'models/auditoriums';
import { isVexpoLiveStream } from 'utils/streamableLinkUtils';
import useJoinWebinarTracking from 'hooks/useJoinWebinarTracking';
import useTwilioEventFunctions from 'components/twilio/hooks/useTwilioEventFunctions';
import AuditoriumWrapper from './AuditoriumWrapper';
import FollowingWebinarItem from './FollowingWebinarItem';
import WebinarMainContentContainer from './WebinarMainContentContainer';
import { CircularLoadingHiderComponent } from '../../components/indicators/CircularLoadingIndicator';
import PageNoFound from '../PageNoFound';
import WebinarFloatingSlido from './slido/WebinarFloatingSlido';

const ContentContainer = styled.div`
  display: flex;
  padding: ${({ hasAuditorium }) => ((hasAuditorium) ? 0 : '40px 80px')};
  flex-direction: ${({ hasAuditorium }) => (hasAuditorium ? 'column' : 'row')};

  @media (max-width: 1200px) {
    padding: ${({ hasAuditorium }) => ((hasAuditorium) ? 0 : '40px 40px')};
  }

  @media (max-width: 960px) {
    padding: 0;
  }
`;

const FollowingWebinarContainer = styled.div`
  margin-left: ${({ hasAuditorium }) => (hasAuditorium ? '0' : '40px')};
  width: ${({ hasAuditorium }) => (hasAuditorium ? '100%' : '360px')};
  border: 1px solid #DBDEE2;
  height: ${({ hasAuditorium }) => (hasAuditorium ? 'initial' : '420px')};;
  overflow-y: auto;

  @media (max-width: 960px) {
    display: none;
  }
`;

const WebinarLabelContainer = styled.div`
  padding: 10px 30px 10px 20px;
  background-color: #FAFAFA;
  user-select: none;

  font-style: normal;
  font-weight: bold;
  font-size: 14px;
  line-height: 14px;
  color: #999;
`;

const FooterContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 16px;
`;

const WebinarList = ({ webinarId, webinars, label }) => (
  <>
    <WebinarLabelContainer>
      {label}
    </WebinarLabelContainer>
    {
      webinars.map((followingWebinar) => (
        <FollowingWebinarItem
          key={followingWebinar.id}
          webinar={followingWebinar}
          isCurrent={followingWebinar.id === webinarId}
        />
      ))
    }
  </>
);

WebinarList.propTypes = {
  webinarId: PropTypes.string.isRequired,
  webinars: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  label: PropTypes.string.isRequired,
};

const WebinarListMemo = React.memo(WebinarList);

const WebinarDetailContainer = () => {
  const { webinarId } = useParams();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const locale = useSelector(selectLocale);
  const { features } = useSelector((s) => s.eventConfigs);
  const liveWebinars = useSelector(selectAllLiveWebinars());
  const onDemandWebinars = useSelector(selectAllOnDemandWebinars());
  const webinar = useSelector((s) => selectWebinarById(s, webinarId));
  const auditorium = useSelector((s) => selectAuditoriumByWebinarId(s, webinarId));
  const eventName = useSelector(selectLocalizedEventName);
  const sortedLiveWebinars = useMemo(() => liveWebinars.sort(webinarSortFn(locale)), [liveWebinars, locale]);
  const { joinGroupChat: twilioJoinGroupChat } = useTwilioEventFunctions();
  const hasAuditorium = useMemo(() => features.includes('auditoriumWebinar'), [features]);
  const isLoading = useMemo(() => (!!webinar && (hasAuditorium ? !!auditorium : true)), [webinar, hasAuditorium, auditorium]);

  useJoinWebinarTracking(webinarId);

  useEffect(() => {
    if (hasAuditorium) {
      dispatch(fetchAuditoriumById(webinarId));
    }
  }, [dispatch, webinarId, hasAuditorium]);

  const webinarTitle = useMemo(() => (webinar?.translations?.[locale]?.title), [webinar, locale]);
  const isVepoWebinar = useMemo(() => (isVexpoLiveStream(webinar?.translations?.[locale]?.url)), [webinar, locale]);

  useEffect(() => {
    if (isVepoWebinar) {
      const vexpoWebinarUrl = new URL(webinar?.translations?.[locale]?.url);
      const vexpoWebinarId = vexpoWebinarUrl.pathname.split('/').pop();
      twilioJoinGroupChat(`agoraWebinar:${vexpoWebinarId}`, {
        groupChatType: GroupChatType.Webinar,
        displayInterface: true,
      });
    }
  }, [isVepoWebinar, twilioJoinGroupChat, webinar, locale]);

  useEffect(() => {
    if (webinar) {
      window.trackingItemToFirebase(null, 'Webinar', 'Session_DetailPage_Enter', 'SessionInfo', webinar.url || '', webinarId, webinarTitle);
    }
  }, [webinar, webinarId, webinarTitle]);

  if (sortedLiveWebinars.length > 0 && !webinar) {
    return (<PageNoFound />);
  }

  return (
    <>
      <CustomHelmet>
        <meta charSet="utf-8" />
        <title>{`${webinarTitle || 'Webinar Not Found'} | ${eventName}`}</title>
        <meta name="description" content={`Detail of ${webinarTitle} ${webinar} in ${eventName}.`} />
      </CustomHelmet>
      <CircularLoadingHiderComponent condition={isLoading}>
        <AuditoriumWrapper>
          <ContentContainer hasAuditorium={hasAuditorium} id="webinar-content-container">
            <WebinarMainContentContainer webinar={webinar} />
            <FollowingWebinarContainer hasAuditorium={hasAuditorium} id="following-webinar-container">
              {Boolean(sortedLiveWebinars.length) && (
                <WebinarListMemo
                  webinars={sortedLiveWebinars}
                  webinarId={webinarId}
                  label={t('webinar.live', 'Live')}
                />
              )}
              {Boolean(onDemandWebinars.length) && (
                <WebinarListMemo
                  webinars={onDemandWebinars}
                  webinarId={webinarId}
                  label={t('webinar.on_demand', 'On Demand')}
                />
              )}
            </FollowingWebinarContainer>
          </ContentContainer>
          <FooterContainer>
            <FloatingFooter hashState={Routes.webinar} />
          </FooterContainer>
        </AuditoriumWrapper>
      </CircularLoadingHiderComponent>
    </>
  );
};

export default WebinarDetailContainer;
